import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ledgerApi } from '@/services/api';

const DATA_TABS = [
  { id: 'accounts', label: 'Comptes' },
  { id: 'paymentMethods', label: 'Moyens de paiement' },
  { id: 'bankDetails', label: 'Coordonnees bancaires' },
  { id: 'journals', label: 'Journaux' },
  { id: 'transactions', label: 'Transactions' },
];

function toArray(payload, candidates = []) {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== 'object') return [];
  for (const key of candidates) {
    if (Array.isArray(payload[key])) return payload[key];
  }
  for (const value of Object.values(payload)) {
    if (Array.isArray(value)) return value;
  }
  return [];
}

function niceLabel(key) {
  return String(key || '')
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (c) => c.toUpperCase());
}

/** Contexte org/projet déjà implicite dans Entrea : masquer ces champs dans l’UI. */
function shouldHideOrgContextKey(key) {
  const k = String(key || '').trim();
  if (!k) return true;
  const low = k.toLowerCase();
  if (low === 'organization_id' || low === 'org_id' || low === 'organisation_id') return true;
  if (low === 'organizationid' || low === 'orgid') return true;
  if (low.endsWith('_organization_id') || low.endsWith('_org_id')) return true;
  if (low === 'tenant_id' || low === 'tenantid') return true;
  if (low === 'project_id' || low === 'projectid') return true;
  return false;
}

function preferredColumnsForTab(activeTab) {
  if (activeTab === 'transactions') {
    return [
      'id',
      'description',
      'memo',
      'narration',
      'label',
      'reference',
      'kind',
      'type',
      'status',
      'amount',
      'currency',
      'created_at',
      'updated_at',
    ];
  }
  return ['id', 'name', 'label', 'kind', 'status', 'amount', 'currency', 'created_at', 'updated_at'];
}

function cellText(value) {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function filterRows(rows, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((row) =>
    Object.values(row || {}).some((value) => cellText(value).toLowerCase().includes(q)),
  );
}

export default function AdminLedgera() {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  const [meta, setMeta] = useState({});
  const [summary, setSummary] = useState({});
  const [accounts, setAccounts] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [bankDetails, setBankDetails] = useState([]);
  const [journals, setJournals] = useState([]);
  const [transactions, setTransactions] = useState([]);

  const loadLedgera = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [metaRes, summaryRes, accountsRes, pmRes, bdRes, journalsRes, txRes] = await Promise.all([
        ledgerApi.getMeta(),
        ledgerApi.getStatsSummary(),
        ledgerApi.listAccounts({ include_inactive: true }),
        ledgerApi.listPaymentMethods({ include_inactive: true, limit: 200 }),
        ledgerApi.listBankDetails({ limit: 200 }),
        ledgerApi.listJournals({ limit: 200 }),
        ledgerApi.listTransactions({ limit: 200 }),
      ]);

      setMeta(metaRes?.data || {});
      setSummary(summaryRes?.data || {});
      setAccounts(toArray(accountsRes?.data, ['accounts', 'items', 'results']));
      setPaymentMethods(toArray(pmRes?.data, ['payment_methods', 'items', 'results']));
      setBankDetails(toArray(bdRes?.data, ['bank_details', 'items', 'results']));
      setJournals(toArray(journalsRes?.data, ['journals', 'items', 'results']));
      setTransactions(toArray(txRes?.data, ['transactions', 'items', 'results']));
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Erreur de chargement Ledgera.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLedgera();
  }, [loadLedgera]);

  const counts = useMemo(
    () => ({
      accounts: accounts.length,
      paymentMethods: paymentMethods.length,
      bankDetails: bankDetails.length,
      journals: journals.length,
      transactions: transactions.length,
    }),
    [accounts.length, paymentMethods.length, bankDetails.length, journals.length, transactions.length],
  );

  const currentRows = useMemo(() => {
    if (activeTab === 'accounts') return accounts;
    if (activeTab === 'paymentMethods') return paymentMethods;
    if (activeTab === 'bankDetails') return bankDetails;
    if (activeTab === 'journals') return journals;
    if (activeTab === 'transactions') return transactions;
    return [];
  }, [activeTab, accounts, paymentMethods, bankDetails, journals, transactions]);

  const filteredRows = useMemo(() => filterRows(currentRows, query), [currentRows, query]);
  const selectedDataTab = DATA_TABS.find((tab) => tab.id === activeTab);

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-indigo-100 bg-gradient-to-r from-indigo-50 via-white to-purple-50 p-4 sm:p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Ledgera Admin</h1>
            <p className="text-sm text-gray-600 mt-1">
              Console de pilotage Ledgera: comptes, transactions et parametres de paiement.
            </p>
          </div>
          <button
            type="button"
            onClick={loadLedgera}
            className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition disabled:opacity-70"
            disabled={loading}
          >
            {loading ? 'Chargement...' : 'Rafraichir'}
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <Stat label="Comptes" value={counts.accounts} accent="indigo" />
        <Stat label="Moyens de paiement" value={counts.paymentMethods} accent="emerald" />
        <Stat label="Coordonnees bancaires" value={counts.bankDetails} accent="sky" />
        <Stat label="Journaux" value={counts.journals} accent="amber" />
        <Stat label="Transactions" value={counts.transactions} accent="violet" />
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-2 shadow-sm">
        <div className="flex flex-wrap gap-2">
          <TabButton
            label="Vue globale"
            isActive={activeTab === 'overview'}
            onClick={() => setActiveTab('overview')}
          />
          {DATA_TABS.map((tab) => (
            <TabButton
              key={tab.id}
              label={tab.label}
              badge={counts[tab.id]}
              isActive={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
            />
          ))}
        </div>
      </div>

      {activeTab === 'overview' ? (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <KeyValues title="Meta Ledgera" data={meta} />
          <KeyValues title="Stats Summary" data={summary} />
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-lg font-semibold text-gray-900">
              {selectedDataTab?.label} ({currentRows.length})
            </h2>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher dans les donnees..."
              className="w-full sm:w-80 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
          </div>
          <DataTable
            rows={filteredRows}
            activeTab={activeTab}
            emptyLabel={query ? 'Aucun resultat pour ce filtre.' : 'Aucune donnee.'}
          />
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent = 'indigo' }) {
  const accents = {
    indigo: 'bg-indigo-50 text-indigo-700',
    emerald: 'bg-emerald-50 text-emerald-700',
    sky: 'bg-sky-50 text-sky-700',
    amber: 'bg-amber-50 text-amber-700',
    violet: 'bg-violet-50 text-violet-700',
  };
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${accents[accent] || accents.indigo}`}>
        {label}
      </div>
      <div className="text-2xl font-semibold text-gray-900 mt-2">{value}</div>
    </div>
  );
}

function TabButton({ label, badge, isActive, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition ${
        isActive ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'
      }`}
    >
      <span>{label}</span>
      {typeof badge === 'number' && (
        <span className={`rounded-full px-2 py-0.5 text-xs ${isActive ? 'bg-white/20' : 'bg-gray-200 text-gray-700'}`}>
          {badge}
        </span>
      )}
    </button>
  );
}

function KeyValues({ title, data }) {
  const entries = Object.entries(data || {}).filter(([key]) => !shouldHideOrgContextKey(key));
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <h2 className="text-base font-semibold text-gray-900 mb-3">{title}</h2>
      {entries.length === 0 ? (
        <p className="text-sm text-gray-500">Aucune donnee.</p>
      ) : (
        <div className="grid grid-cols-1 gap-2">
          {entries.map(([key, value]) => (
            <div key={key} className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{niceLabel(key)}</p>
              <p className="text-sm text-gray-800 break-words mt-0.5">{cellText(value)}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DataTable({ rows, emptyLabel, activeTab }) {
  const columns = useMemo(() => {
    const keys = new Set();
    const preferredOrder = preferredColumnsForTab(activeTab);
    const maxCols = activeTab === 'transactions' ? 9 : 8;
    rows.slice(0, 30).forEach((row) =>
      Object.keys(row || {}).forEach((key) => {
        if (!shouldHideOrgContextKey(key)) keys.add(key);
      }),
    );
    const found = Array.from(keys);
    const first = preferredOrder.filter((k) => keys.has(k));
    const rest = found.filter((k) => !preferredOrder.includes(k)).slice(0, maxCols - first.length);
    return [...first, ...rest].slice(0, maxCols);
  }, [rows, activeTab]);

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      {rows.length === 0 ? (
        <div className="px-4 py-8 text-sm text-gray-500 text-center">{emptyLabel}</div>
      ) : (
        <div className="overflow-auto max-h-[65vh]">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">#</th>
                {columns.map((col) => (
                  <th key={col} className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    {niceLabel(col)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row, idx) => (
                <tr key={row?.id || row?._id || idx} className="hover:bg-gray-50">
                  <td className="px-3 py-2 text-xs text-gray-500 align-top">{idx + 1}</td>
                  {columns.map((col) => {
                    const text = cellText(row?.[col]);
                    const isWide =
                      activeTab === 'transactions' &&
                      ['description', 'memo', 'narration', 'label'].includes(String(col).toLowerCase());
                    return (
                      <td
                        key={`${idx}-${col}`}
                        className={`px-4 py-2 text-sm text-gray-700 align-top ${isWide ? 'max-w-md' : 'max-w-xs'}`}
                      >
                        <div
                          className={
                            isWide ? 'max-h-24 overflow-y-auto break-words whitespace-pre-wrap' : 'truncate'
                          }
                          title={text}
                        >
                          {text}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
